import { useCallback, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import FestivalMatchSetupModal from '../components/lineup/FestivalMatchSetupModal';

/**
 * Shared festival queue-join intercept. Every surface that offers "Match me"
 * (EventDetail, EventsList cards, useEventRsvpParity) routes the queue choice
 * through this hook so festivals always collect attendance days + lineup votes
 * before entering the match queue.
 *
 * Usage:
 *   const { maybeInterceptFestivalQueue, festivalSetupModal } = useFestivalMatchSetup(userId);
 *   // in the queue handler:
 *   if (choice === 'queue' && await maybeInterceptFestivalQueue(eventId, proceed)) return;
 *   // render {festivalSetupModal} alongside the other modals
 */
export function useFestivalMatchSetup(userId) {
  const [modalState, setModalState] = useState({ show: false, eventId: null, eventName: null });
  const continueRef = useRef(null);

  const maybeInterceptFestivalQueue = useCallback(
    async (eventId, onContinue) => {
      if (!userId || !eventId) return false;
      const { data, error } = await supabase
        .from('events')
        .select('event_type, display_name, display_name_override, event_name')
        .eq('event_id', eventId)
        .maybeSingle();
      if (error || (data?.event_type || '').toLowerCase() !== 'festival') return false;

      continueRef.current = onContinue;
      setModalState({
        show: true,
        eventId,
        eventName: data.display_name_override || data.display_name || data.event_name || null,
      });
      return true;
    },
    [userId],
  );

  const closeModal = () => {
    continueRef.current = null;
    setModalState({ show: false, eventId: null, eventName: null });
  };

  const festivalSetupModal = (
    <FestivalMatchSetupModal
      isOpen={modalState.show}
      onClose={closeModal}
      eventId={modalState.eventId}
      eventName={modalState.eventName}
      userId={userId}
      onComplete={async () => {
        const proceed = continueRef.current;
        continueRef.current = null;
        setModalState({ show: false, eventId: null, eventName: null });
        await proceed?.();
      }}
    />
  );

  return { maybeInterceptFestivalQueue, festivalSetupModal };
}
